import React, { FC, useState } from 'react';

import { Box, Divider, Typography } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { FormikProps } from 'formik';
import { DateTime } from 'luxon';

import { weekdayWeekendCount } from '@pharmaplan/common/helpers/utils';

import RangeCalendar from '../../../../../common/RangeCalendar';
import DateRange from '../../../../../common/CustomDateRange';
import strings from '../../../../../../localization';

import styles from './styles';
import { toJSDate } from '../../../../../Dashboard/MainCalendar/helpers';

interface IViewEditContractDate {
  openedDate?: string;
  formik: FormikProps<any>;
  isUpdate?: boolean;
  onDateChange: (
    startDate: DateTime | string,
    endDate: DateTime | string
  ) => void;
}

const ViewEditContractDate: FC<IViewEditContractDate> = ({
  formik,
  openedDate,
  onDateChange,
  isUpdate,
}) => {
  const [selectedDates, setSelectedDates] = useState<
    [Date | null, Date | null]
  >([
    toJSDate(formik.values.startDate?.toISO({ includeOffset: false })),
    toJSDate(formik.values.endDate?.toISO({ includeOffset: false })),
  ]);

  const handleRangeChange = (dateRange: Array<Date>) => {
    const start = DateTime.fromJSDate(dateRange[0]);
    const end = DateTime.fromJSDate(dateRange[1]);
    onDateChange(start, end);

    formik.setFieldValue('startDate', start);
    formik.setFieldValue('endDate', end);

    setSelectedDates(dateRange as [Date, Date]);
  };

  const handleDate = (isStart: boolean) =>
    (date?: DateTime | null) => {
      const jsDate = date?.toJSDate() ?? new Date();
      if (isStart) {
        setSelectedDates([jsDate, selectedDates[1]]);
      } else {
        setSelectedDates([selectedDates[0], jsDate]);
      }
    };

  let weekdays = 0;
  let weekends = 0;

  if (selectedDates[0] && selectedDates[1]) {
    const counts = weekdayWeekendCount(
      DateTime.fromJSDate(selectedDates?.[0]),
      DateTime.fromJSDate(selectedDates?.[1]),
    );

    weekdays = counts.weekdays;
    weekends = counts.weekends;
  }

  return (
    <>
      <Box sx={styles.container}>
        <RangeCalendar
          openedDate={openedDate}
          value={selectedDates}
          onChange={handleRangeChange}
          isUpdate={isUpdate}
        />
        <Divider sx={styles.divider} />
        <Box sx={styles.dateContainer}>
          <DateRange
            customClass={{}}
            customStartHandler={handleDate(true)}
            customEndHandler={handleDate(false)}
            startName="startDate"
            endName="endDate"
            preventPast
            xs={6}
            disabled
            formik={formik}
          />
        </Box>
      </Box>
      {selectedDates[0] && selectedDates[1] && (
        <Box sx={styles.footerContainer}>
          <Box sx={[styles.weekContainer, styles.weekDayContainer]}>
            <CheckCircle sx={styles.icon} />
            <Typography
              sx={styles.weekText}
            >
              {`${strings.weekdays}: ${weekdays}`}
            </Typography>
          </Box>
          <Box sx={[styles.weekContainer, styles.weekDayContainer]}>
            <CheckCircle sx={styles.icon} />
            <Typography
              sx={styles.weekText}
            >
              {`${strings.weekends}: ${weekends}`}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ViewEditContractDate;
